import { useCallback, useMemo } from 'react';

import { useErpSurveyResponseDetailLazyQuery } from 'core/graphql';

import { ISurveyResponseDetail, parseResponseDetail } from './surveyModel';

interface ISurveyResponseDetailHook {
  load: (responseId: string) => void;
  detail: ISurveyResponseDetail | null;
  loading: boolean;
  error?: string;
  /** Повторить последний запрос после сбоя (окно «Ответы»). */
  retry: () => void;
}

/**
 * Spec 0011 — one student's named answers, fetched lazily when the operator
 * opens the answers window on the results page. The JSONString `value` of every
 * answer is decoded here (transform layer) so components receive plain
 * bool / int / str / int[] values and never touch `JSON.parse`.
 */
export const useSurveyResponseDetail = (): ISurveyResponseDetailHook => {
  const [
    run,
    { data, loading, error, refetch },
  ] = useErpSurveyResponseDetailLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const load = useCallback(
    (responseId: string) => {
      run({ variables: { responseId } });
    },
    [run],
  );

  const detail = useMemo(
    () =>
      data?.surveyResponseDetail
        ? parseResponseDetail(data.surveyResponseDetail)
        : null,
    [data?.surveyResponseDetail],
  );

  const retry = useCallback(() => {
    // `refetch` есть только после первого вызова; сбой бывает только после него.
    // Сбой повтора покажет тот же `error` — промис глушим, чтобы не было
    // необработанного отказа.
    refetch?.().catch(() => undefined);
  }, [refetch]);

  return { load, detail, loading, error: error?.message, retry };
};
